import { useEffect, useMemo, useRef, useState } from 'react'
import { History, Redo2, Undo2, X } from 'lucide-react'
import { useAppStore } from '@core/store'
import { podeDesfazer, podeRefazer } from '@core/store/historySlice'

// Hub-wide undo/redo dropdown. Lives in the topbar so any module that
// pushes a HistoryEntry (BoQ today, IFCEdit tomorrow) gets a single,
// consistent surface for the user. Modules supply the undo/redo
// callbacks; this component just lists them and dispatches.

const MODULE_LABELS: Record<string, string> = {
  boq: 'BoQ',
  'ifc-edit': 'IFCEdit',
  bcf: 'BCF',
}

function moduleLabel(slug: string): string {
  return MODULE_LABELS[slug] ?? slug
}

function HistoryButton() {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement | null>(null)

  const history = useAppStore((s) => s.history)
  const setHistoryEntryUndone = useAppStore((s) => s.setHistoryEntryUndone)
  const removeHistoryEntry = useAppStore((s) => s.removeHistoryEntry)
  const clearHistory = useAppStore((s) => s.clearHistory)

  const sorted = useMemo(
    () => [...history].sort((a, b) => b.ts - a.ts),
    [history],
  )
  const undoableCount = sorted.filter((e) => !e.undone).length
  const redoableCount = sorted.filter((e) => e.undone).length

  // Click-outside to close.
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const onUndo = async (id: string) => {
    const entry = history.find((h) => h.id === id)
    if (!entry || !entry.undo || busy || !podeDesfazer(history, id)) return
    setBusy(id)
    setError(null)
    try {
      await entry.undo()
      setHistoryEntryUndone(id, true)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(null)
    }
  }

  const onRedo = async (id: string) => {
    const entry = history.find((h) => h.id === id)
    if (!entry || !entry.redo || busy || !podeRefazer(history, id)) return
    setBusy(id)
    setError(null)
    try {
      await entry.redo()
      setHistoryEntryUndone(id, false)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 rounded border border-[var(--color-border)] bg-[var(--color-surface)]/60 px-1.5 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        title="Histórico de alterações nesta sessão (undo/redo cross-módulo)"
        aria-label="Histórico de alterações"
      >
        <History size={15} />
        {undoableCount > 0 && (
          <span className="rounded bg-[var(--color-accent-bg)] px-1 text-[10px] font-medium text-[var(--color-accent)]">
            {undoableCount}
          </span>
        )}
        {redoableCount > 0 && (
          <span className="text-[10px] text-[var(--color-text-dim)]">
            ({redoableCount})
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-[900] mt-1 w-[360px] rounded-md border border-[var(--color-border)] bg-[var(--color-panel)] p-2 text-xs shadow-lg">
          <div className="mb-2 flex items-center justify-between px-1">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
              Histórico desta sessão
            </span>
            {sorted.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Limpar histórico? Não desfaz acções já aplicadas.')) {
                    clearHistory()
                  }
                }}
                className="text-[10px] text-[var(--color-text-dim)] hover:text-red-400"
              >
                Limpar
              </button>
            )}
          </div>
          {sorted.length === 0 ? (
            <p className="px-2 py-3 text-center text-[10px] text-[var(--color-text-dim)]">
              Sem acções registadas. Quando escreveres dados num IFC
              através dum módulo, podes desfazer aqui.
            </p>
          ) : (
            <ul className="max-h-72 space-y-0.5 overflow-auto">
              {sorted.map((e) => (
                <li
                  key={e.id}
                  className={`flex items-center gap-2 rounded px-2 py-1 text-[10px] ${
                    e.undone
                      ? 'text-[var(--color-text-dim)]'
                      : 'text-[var(--color-text)]'
                  } hover:bg-[var(--color-bg)]/50`}
                >
                  <span className="rounded bg-[var(--color-bg)]/60 px-1 text-[9px] uppercase tracking-wide text-[var(--color-text-muted)]">
                    {moduleLabel(e.module)}
                  </span>
                  <span
                    className="flex-1 truncate"
                    title={`${e.label} · ${new Date(e.ts).toLocaleString()}`}
                  >
                    {e.label}
                  </span>
                  {e.undone ? (
                    <button
                      type="button"
                      onClick={() => void onRedo(e.id)}
                      disabled={!podeRefazer(history, e.id) || busy === e.id}
                      title={
                        podeRefazer(history, e.id)
                          ? 'Refazer'
                          : 'Refaça primeiro as acções anteriores deste módulo'
                      }
                      className="rounded border border-[var(--color-border)] px-1.5 py-0 text-[10px] text-[var(--color-accent)] hover:bg-[var(--color-accent)]/10 disabled:opacity-30"
                    >
                      <Redo2 size={10} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void onUndo(e.id)}
                      disabled={!podeDesfazer(history, e.id) || busy === e.id}
                      title={
                        podeDesfazer(history, e.id)
                          ? 'Desfazer'
                          : 'Desfaça primeiro as acções mais recentes deste módulo'
                      }
                      className="rounded border border-[var(--color-border)] px-1.5 py-0 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-30"
                    >
                      <Undo2 size={10} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => removeHistoryEntry(e.id)}
                    title="Remover do histórico (sem afectar o IFC)"
                    className="rounded px-1 text-[var(--color-text-dim)] hover:text-red-400"
                  >
                    <X size={10} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {error && (
            <p className="mt-1 rounded border border-[var(--color-error-border)] bg-[var(--color-error-bg)] px-2 py-1 text-[10px] text-[var(--color-error-text)]">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default HistoryButton
